import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { IDataSourceApiService, IEntity, IEntityMetadata, IEnvironment, INamespace, IRuntimeEnvironment } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { forkJoin, Observable, of } from 'rxjs';
import { concatMap, map, mergeAll, toArray } from 'rxjs/operators';
import { Identity, IdentityStorage } from 'storage-service-lib';

interface INamespaceMeta {
  namespace: string;
  objectNames: IEntityLabelPair[];
}

interface IEntityLabelPair {
  objectName: string;
  label: string;
}

enum InveniasEntityLabels {
  peopleListItem = 'People',
  companiesListItem = 'Companies',
  assignmentsListItem = 'Assignments',
  programmesListItem = 'Programmes',
}

export const InveniasNamespace: INamespace = {
  name: 'invenias',
  aliases: ['inveniasnextgen'],
};

export enum InveniasEntityTypes {
  People = 'people',
  Companies = 'companies',
  Programmes = 'programmes',
  Assignments = 'assignments',
  Documents = 'documents',
}

export enum InveniasListTypes {
  Documents = 'documents',
  Candidates = 'candidates',
}

export enum InveniasFileTypes {
  Document = 'document',
}

@Injectable({ providedIn: 'root' })
export class InveniasDataSourceApiService implements IDataSourceApiService {
  private static DEFAULT_SELECT = {};
  private readonly inveniasApiUrl: string;

  constructor(
    private http: HttpClient,
    private identityStorage: IdentityStorage,
    @Inject(ENVIRONMENT) private environment: Partial<IEnvironment>,
    @Inject(RUNTIME_ENVIRONMENT) private runtimeEnvironment: IRuntimeEnvironment | undefined
  ) {
    if (this.runtimeEnvironment && !this.runtimeEnvironment.jwt) {
      throw new Error('Token missing. Sign-in may have failed.');
    }
    this.inveniasApiUrl = InveniasDataSourceApiService.getUrlEndpoint((this.identityStorage.get() as Identity).token);
  }

  ping(): Observable<boolean> {
    const url = `${this.inveniasApiUrl}/monitor`;
    return this.http.get<any>(url).pipe(map((_) => true));
  }

  get(entity: IEntity): Observable<any> {
    const url = `${this.inveniasApiUrl}/${entity.type}/${entity.id}`;
    return this.http.get<any>(url).pipe(
      // force the response data to match the IEntity interface
      map((fatEntity) => ({ ...fatEntity, id: entity.id, type: entity.type }))
    );
  }

  query(entityType: string, queryParams: { searchTerm: string }): Observable<IEntity[]> {
    const searchTerm: string = queryParams.searchTerm.trim();

    // Invenias has a minimum search length of 2 - returns 400 Bad Request
    if (InveniasDataSourceApiService.queryLessThanMinLength(entityType, searchTerm)) {
      return of([]);
    }

    const url = `${this.inveniasApiUrl}/quicksearch/${entityType}?request.searchTerm=${searchTerm}`;
    return this.http.get<any[]>(url).pipe(
      // force the response data to match the IEntity interface
      map((response) =>
        response.map((entity) => ({
          ...entity,
          id: entity.ItemId,
          type: entityType,
        }))
      )
    );
  }

  list(listParams: any): Observable<any[]> {
    const url = `${this.inveniasApiUrl}/${listParams.entity.type}/${listParams.entity.id}/${listParams.associatedType}/list`;
    const selectFields = listParams.fields ? { Select: listParams.fields } : InveniasDataSourceApiService.DEFAULT_SELECT;

    const buildObs = (first: any) => {
      const totalToPull = first.Paging.TotalItemCount;
      const pageSize = 100;
      const pageCount = Math.ceil(totalToPull / pageSize);
      const obs: any[] = [of(first)];
      for (let i = 1; i < pageCount; i++) {
        const body = { PageSize: pageSize, PageIndex: i, ...selectFields };
        obs.push(this.http.post<any>(url, body));
      }
      return obs;
    };

    const first$ = this.http.post<any>(url, selectFields);
    return first$.pipe(
      map((first) => buildObs(first)),
      concatMap((obs) =>
        forkJoin(...obs).pipe(
          concatMap((r) => r.map((rr) => (rr as { Items: any }).Items)),
          mergeAll(),
          toArray()
        )
      )
    );
  }

  meta(): Observable<IEntityMetadata[]> {
    const url = `${this.environment.kpHost}/datasource/${InveniasNamespace.name}/meta?labels=true`;
    const entities$: Observable<INamespaceMeta> = this.http.get(url) as Observable<INamespaceMeta>;
    return entities$.pipe(
      map((response) => {
        return response.objectNames.map((entity) => ({
          name: InveniasEntityLabels[entity.objectName as keyof typeof InveniasEntityLabels],
          label: entity.label,
        }));
      })
    );
  }

  settings(settings: string): Observable<any> {
    const url = `${this.inveniasApiUrl}/settings/${settings}`;
    return this.http.get<any>(url);
  }

  buildApiUrl(path: string): string {
    return path;
  }

  private static getUrlEndpoint(token: string): string {
    const jwtPayload = jwtDecode<any>(token);
    const endpointUrl = jwtPayload['http://www.invenias.com/claims/EndpointUrl'];
    return `https://${endpointUrl}/api/v1`;
  }

  private static queryLessThanMinLength(entityType: string, queryParams: string): boolean {
    if (queryParams.length < 2) {
      return true;
    }

    return entityType === InveniasEntityTypes.Programmes && queryParams.length < 3;
  }
}
